import React from 'react';
import { Box, Card, Container, Typography } from '@material-ui/core';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { useQuery } from '@apollo/react-hooks';
import IKImage from '../IKImage/IKImage';
import Swiper from 'react-id-swiper';
import cx from 'classnames';

import { GET_LIST_PARTNERS_BY_FILTER } from '../../graphql/partners';
import { BREAKPOINTS_VALUES } from '../../theme';
import useStyles from './styles';
import { Loader } from '../Loader';
import { translate } from '../../utils/helpers';

const swiperSettings = {
  slidesPerView: 2.5,
  spaceBetween: 16,
  slidesOffsetAfter: 200,
  noSwiping: true,
  breakpoints: {
    [BREAKPOINTS_VALUES.xl]: {
      slidesPerView: 6,
    },
    [BREAKPOINTS_VALUES.lg]: {
      slidesPerView: 4,
    },
    [BREAKPOINTS_VALUES.md]: {
      slidesPerView: 3.5,
      noSwiping: false,
      mousewheel: {
        releaseOnEdges: true,
        forceToAxis: true,
        invert: true,
      },
    },
    [BREAKPOINTS_VALUES.sm]: {
      slidesPerView: 3.1,
      noSwiping: false,
      mousewheel: {
        releaseOnEdges: true,
        forceToAxis: true,
        invert: true,
      },
    },
    [BREAKPOINTS_VALUES.xs]: {
      slidesPerView: 2.5,
      noSwiping: false,
      mousewheel: {
        releaseOnEdges: true,
        forceToAxis: true,
        invert: true,
      },
    },
  },
};

const getBlockByType = (partners, type, classes, heading, description, whiteBg, isMobile) => {
  switch (type) {
    case 'Block':
      return (
        <Box className={cx(classes.cardsWrapper, 'partners-type-block')}>
          <Typography variant="h2" component="h2" align="center" className={classes.headingBlock}>
            {heading}
          </Typography>

          {partners?.map((item) => {
            return (
              <Card key={item.id} className={classes.card}>
                <IKImage
                  path={item.image_url}
                  alt={item.title}
                  draggable={false}
                  srcset={[{ width: 170 }, { width: 270 }, { width: 140 }, { width: 180 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 170px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 140px, 180px`}
                />
              </Card>
            );
          })}
        </Box>
      );
    case 'Slider':
      return isMobile ? (
        <Box className={cx(classes.cardsWrapper, 'partners-type-block')}>
          <Typography variant="h2" component="h2" align="center" className={classes.headingBlock}>
            {heading}
          </Typography>

          {partners?.map((item) => {
            return (
              <Card key={item.id} className={classes.card}>
                <IKImage
                  path={item.image_url}
                  alt={item.title}
                  draggable={false}
                  srcset={[{ width: 170 }, { width: 270 }, { width: 140 }, { width: 180 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 170px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 140px, 180px`}
                />
              </Card>
            );
          })}
        </Box>
      ) : (
        <div className={cx(classes.swiperWrap, 'partners-type-slider')}>
          <Swiper {...swiperSettings}>
            {partners?.map((item) => {
              return (
                <Card
                  key={item.id}
                  className={cx(classes.card, classes.cardSlider, whiteBg && classes.whiteBg)}
                >
                  <IKImage
                    path={item.image_url}
                    alt={item.title}
                    draggable={false}
                    srcset={[{ width: 200 }, { width: 270 }, { width: 320 }, { width: 220 }]}
                    sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 200px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 320px, 220px`}
                  />
                </Card>
              );
            })}
          </Swiper>
        </div>
      );
    case 'WithDescription':
      return (
        <Box className={cx(classes.partnersDescWrapper, 'partners-type-description')}>
          {heading && (
            <Typography variant="h2" component="h2" align="center" className={classes.headingDesc}>
              {heading}
            </Typography>
          )}

          {description && (
            <Typography className={classes.partnerDescBlockDesc}>{description}</Typography>
          )}

          <Box className={classes.cardsDescWrapper}>
            {partners?.map((item) => {
              return (
                <Card key={item?.id} className={classes.cardDesc}>
                  <div className={cx(classes.descImgWrap, whiteBg && classes.whiteBg)}>
                    <IKImage
                      path={item.image_url}
                      alt={item.title}
                      draggable={false}
                      srcset={[{ width: 500 }, { width: 600 }, { width: 850 }, { width: 900 }]}
                      sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 500px, (max-width:${BREAKPOINTS_VALUES['md']}px) 600px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 850px, 900px`}
                    />
                  </div>

                  <Typography className={classes.partnerDesc}>{item?.description}</Typography>
                </Card>
              );
            })}
          </Box>
        </Box>
      );
    default:
      return <div>{translate('block.partners.empty')}</div>;
  }
};

export const PartnersBlock = ({ data: partnersData, width }) => {
  const background = partnersData?.background;
  const displayType = partnersData?.display;
  const whiteBg = partnersData?.white_icon;
  const isMobile = !isWidthUp('md', width);

  const classes = useStyles(background, displayType)();
  const partnerTypeId = partnersData?.partnerType?.id;

  const {
    data,
    loading,
    // error
  } = useQuery(GET_LIST_PARTNERS_BY_FILTER, {
    variables: {
      filter: {
        type_id: partnerTypeId,
      },
    },
  });

  if (loading) {
    return <Loader />;
  }

  const heading =
    typeof partnersData?.heading === 'string' ? partnersData?.heading : partnersData?.heading?.en;
  const description = partnersData?.description;
  const partners = data?.listPartners;

  return (
    <Box
      className={cx(
        classes.wrapper,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'partners-block',
      )}
    >
      <Container className={classes.container}>
        {heading && (
          <Typography variant="h2" component="h2" align="center" className={classes.heading}>
            {heading}
          </Typography>
        )}

        {getBlockByType(partners, displayType, classes, heading, description, whiteBg, isMobile)}
      </Container>
    </Box>
  );
};

export default withWidth()(PartnersBlock);
